/**
 * metaLeadGen/ageBreakdown.ts
 *
 * Aggregates Meta rows by age group. Shows only real Meta data (spend, optins, clicks).
 * Application/close attribution by age will be added once UTM tracking is in place.
 */

import type { MetaLeadGenRow, MetaLeadGenFilters } from "./types";
import { applyFilters } from "./filters";

// ─────────────────────────────────────────────
// AGE GROUPS — canonical order for all charts
// ─────────────────────────────────────────────
export const AGE_GROUPS = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"] as const;
export type AgeGroup = (typeof AGE_GROUPS)[number];

// ─────────────────────────────────────────────
// OUTPUT TYPE
// ─────────────────────────────────────────────
export interface AgeBreakdownRow {
  age: string;
  spend: number;
  clicks: number;
  impressions: number;
  optins: number;
}

// ─────────────────────────────────────────────
// SERVICE
// ─────────────────────────────────────────────

/**
 * Returns one AgeBreakdownRow per canonical age group, in ascending order.
 * Respects all active filters (date range, campaigns, devices, etc.).
 * Groups unknown / non-standard ages into "65+" as a catch-all.
 */
export function getAgeBreakdown(
  rows: MetaLeadGenRow[],
  filters: MetaLeadGenFilters
): AgeBreakdownRow[] {
  const filtered = applyFilters(rows, filters);

  // Initialise accumulators for all canonical age groups
  const agg: Record<string, { spend: number; leads: number; clicks: number; impressions: number }> = {};
  for (const age of AGE_GROUPS) {
    agg[age] = { spend: 0, leads: 0, clicks: 0, impressions: 0 };
  }

  for (const row of filtered) {
    const key = (AGE_GROUPS as readonly string[]).includes(row.age) ? row.age : "65+";
    agg[key].spend += row.spend;
    agg[key].leads += row.leads;
    agg[key].clicks += row.clicks;
    agg[key].impressions += row.impressions;
  }

  return AGE_GROUPS.map(age => {
    const { spend, leads: optins, clicks, impressions } = agg[age];
    return { age, spend, clicks, impressions, optins };
  });
}
